import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { FileText, Plus, Eye } from 'lucide-react'
import { formatDate, formatCurrency } from '@/lib/utils'
import type { Devis } from '@/types/database-real'

interface ClientQuotesTabProps {
  quotes: Devis[]
  clientId: string
}

const statusVariants: Record<string, 'mint' | 'gold' | 'brown' | 'red' | 'default'> = {
  draft: 'default',
  sent: 'gold',
  viewed: 'brown',
  accepted: 'mint',
  rejected: 'red',
  expired: 'brown',
  converted: 'mint',
}

const statusLabels: Record<string, string> = {
  draft: 'Brouillon',
  sent: 'Envoyé',
  viewed: 'Vu',
  accepted: 'Accepté',
  rejected: 'Refusé',
  expired: 'Expiré',
  converted: 'Converti',
}

export function ClientQuotesTab({ quotes, clientId }: ClientQuotesTabProps) {
  if (quotes.length === 0) {
    return (
      <EmptyState
        icon={<FileText className="w-12 h-12" />}
        title="Aucun devis"
        description="Ce client n'a pas encore de devis."
        action={
          <Link to={`/devis/new?client=${clientId}`}>
            <Button leftIcon={<Plus className="w-4 h-4" />}>
              Créer un devis
            </Button>
          </Link>
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Link to={`/devis/new?client=${clientId}`}>
          <Button variant="outline" leftIcon={<Plus className="w-4 h-4" />}>
            Nouveau devis
          </Button>
        </Link>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-beige-50 border-b border-beige-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Numéro
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Titre
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Statut
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Montant HT
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Montant TTC
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Émission
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Validité
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-beige-200">
              {quotes.map((quote) => (
                <tr key={quote.id} className="hover:bg-beige-50">
                  <td className="px-4 py-3 font-medium text-brown-900">
                    {quote.numero || '-'}
                  </td>
                  <td className="px-4 py-3 text-brown-600">
                    {quote.titre || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariants[quote.status || 'draft'] || 'default'}>
                      {statusLabels[quote.status || 'draft'] || quote.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-brown-600">
                    {quote.montant_ht ? formatCurrency(quote.montant_ht) : '-'}
                  </td>
                  <td className="px-4 py-3 font-medium text-brown-900">
                    {quote.montant_ttc ? formatCurrency(quote.montant_ttc) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-brown-500">
                    {quote.date_emission ? formatDate(quote.date_emission) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-brown-500">
                    {quote.date_validite ? formatDate(quote.date_validite) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/devis/${quote.id}`}>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
